import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GiveToMembers } from "@/components/souvenirs/GiveToMembers";
import { MemberGiftsReport } from "@/components/souvenirs/MemberGiftsReport";
import { DistributionProgress } from "@/components/souvenirs/DistributionProgress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { TShirt, X, DownloadSimple, CheckCircle, PaperPlaneTilt, Package, Warning, Truck, Plus, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const keyOf = (r) => r.region || r.target_name || "row";

// Two independent per-item status pills (no combined status)
function ItemBadges({ row }) {
  if (!row.dispatched) {
    return <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-muted text-muted-foreground"
      data-testid={`status-${keyOf(row)}`}>Not dispatched</span>;
  }
  const pill = (label, disp, confirmed, received, short) => {
    let cls = "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400";
    let text = `${label} pending`;
    if (confirmed && short) { cls = "bg-destructive/15 text-destructive"; text = `${label} ⚠ ${received}/${disp}`; }
    else if (confirmed) { cls = "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"; text = `${label} ✓ ${received}`; }
    return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{text}</span>;
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`status-${keyOf(row)}`}>
      {pill("T-shirts", row.tshirts, row.tshirts_confirmed, row.tshirts_received, row.tshirts_short)}
      {pill("Cloth", row.cloth, row.cloth_confirmed, row.cloth_received, row.cloth_short)}
    </div>
  );
}

// Panel for confirming receipt of a single dispatch record, one item at a time
function ReceivedPanel({ data, confirmUrl, reload, testPrefix = "received" }) {
  const [busy, setBusy] = useState("");
  const [vals, setVals] = useState({ tshirts: "", cloth: "" });
  const [notes, setNotes] = useState({ tshirts: "", cloth: "" });

  const confirm = async (item) => {
    setBusy(item);
    try {
      const received = vals[item] === "" ? data[item] : Number(vals[item]) || 0;
      const res = await api.post(confirmUrl, { item, received, note: notes[item].trim() });
      toast.success(res.data.short ? `Confirmed with a shortage flagged` : "Receipt confirmed");
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const ItemBlock = ({ item, label }) => {
    const dispatched = data[item];
    const confirmed = data[`${item}_confirmed`];
    const received = data[`${item}_received`];
    const short = data[`${item}_short`];
    return (
      <div className="rounded-lg border border-border p-4 space-y-3" data-testid={`${testPrefix}-item-${item}`}>
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold" data-testid={`${testPrefix}-${item}-dispatched`}>{dispatched}</p>
        </div>
        {confirmed ? (
          <div className={`rounded-md px-3 py-2 text-sm font-medium ${short ? "bg-destructive/10 text-destructive" : "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400"}`}
            data-testid={`${testPrefix}-${item}-confirmed`}>
            <span className="inline-flex items-center gap-1.5">
              {short ? <Warning size={16} weight="fill" /> : <CheckCircle size={16} weight="fill" />}
              Confirmed {received} received{short ? ` — short of ${dispatched}` : ""}
            </span>
            {data[`${item}_note`] && <p className="text-xs mt-1 opacity-90">Note: {data[`${item}_note`]}</p>}
          </div>
        ) : dispatched > 0 ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input type="number" min={0} placeholder={`Qty received (dispatched ${dispatched})`}
                value={vals[item]} onChange={(e) => setVals((p) => ({ ...p, [item]: e.target.value }))}
                className="h-9" data-testid={`${testPrefix}-${item}-input`} />
              <Button size="sm" disabled={busy === item} onClick={() => confirm(item)}
                data-testid={`${testPrefix}-${item}-confirm`} className="gap-1.5 shrink-0">
                <CheckCircle size={16} weight="bold" /> Confirm
              </Button>
            </div>
            <Input placeholder="Note (optional)" value={notes[item]}
              onChange={(e) => setNotes((p) => ({ ...p, [item]: e.target.value }))}
              className="h-9" data-testid={`${testPrefix}-${item}-note`} />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">None dispatched.</p>
        )}
      </div>
    );
  };

  return (
    <Card className="p-6 space-y-5" data-testid={`${testPrefix}-card`}>
      <div>
        <p className="text-sm text-muted-foreground">
          Dispatched to {data.target_label || data.region}
          {data.dispatched_at ? ` on ${data.dispatched_at.slice(0, 10)}` : ""}
          {data.dispatched_by_name ? ` by ${data.dispatched_by_name}` : ""}
        </p>
        <div className="mt-2"><ItemBadges row={data} /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ItemBlock item="tshirts" label="T-shirts" />
        <ItemBlock item="cloth" label="Cloth (pieces)" />
      </div>
    </Card>
  );
}

const SummaryCard = ({ label, value, testId }) => (
  <Card className="px-4 py-3 min-w-[130px]" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-2xl font-bold mt-0.5 tabular-nums">{value}</p>
  </Card>
);

export default function Souvenirs() {
  const { user } = useAuth();
  const isNational = ["national_manager", "super_admin"].includes(user.role);
  const isRegional = user.role === "regional_manager";
  const isSub = ["facility_manager", "chapter_manager"].includes(user.role);
  const groupBy = (isRegional || isSub) ? "facility" : "region";

  const [view, setView] = useState("breakdown");
  const [filters, setFilters] = useState({ regions: [], facilities: [], chapters: [], scoped_region: null });
  const [facility, setFacility] = useState("");
  const [chapter, setChapter] = useState("");
  const [data, setData] = useState({ sizes: [], rows: [], totals: {}, grand_total: 0, group_by: groupBy });
  const [loading, setLoading] = useState(true);

  // National dispatch (to regions)
  const [dispatch, setDispatch] = useState([]);
  const [dSummary, setDSummary] = useState({});
  const [dDisc, setDDisc] = useState([]);
  const [edits, setEdits] = useState({});
  const [savingRegion, setSavingRegion] = useState("");
  const [addTarget, setAddTarget] = useState(null); // {region, tshirts, cloth}
  const [addingMore, setAddingMore] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null); // {region, list|null}

  const openHistory = async (region) => {
    setHistoryTarget({ region, list: null });
    try {
      const { data } = await api.get("/souvenirs/dispatch/history", { params: { region } });
      setHistoryTarget({ region, list: data.events });
    } catch { setHistoryTarget({ region, list: [] }); }
  };

  // Regional received (from national)
  const [received, setReceived] = useState(null);
  // Sub received (from region)
  const [subReceived, setSubReceived] = useState(null);

  // Regional sub-dispatch (folded into breakdown)
  const [subRows, setSubRows] = useState([]);
  const [subSummary, setSubSummary] = useState({});
  const [subDisc, setSubDisc] = useState([]);
  const [dispatchTarget, setDispatchTarget] = useState(null); // {type,name,label,tshirts,cloth}
  const [savingTarget, setSavingTarget] = useState(false);
  const [timeline, setTimeline] = useState([]);

  const loadTimeline = useCallback(async () => {
    try { setTimeline((await api.get("/souvenirs/timeline")).data.events); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, []);

  const facMap = {}; const chMap = {};
  subRows.forEach((r) => { (r.target_type === "facility" ? facMap : chMap)[r.target_name] = r; });
  const chapterRows = subRows.filter((r) => r.target_type === "chapter");

  const loadDispatch = useCallback(async () => {
    try {
      const { data } = await api.get("/souvenirs/dispatch");
      setDispatch(data.rows); setDSummary(data.summary); setDDisc(data.discrepancies);
      const e = {};
      data.rows.forEach((r) => { e[r.region] = { tshirts: r.tshirts, cloth: r.cloth }; });
      setEdits(e);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, []);

  const loadReceived = useCallback(async () => {
    try { setReceived((await api.get("/souvenirs/received")).data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, []);

  const loadSubReceived = useCallback(async () => {
    try { setSubReceived((await api.get("/souvenirs/subreceived")).data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, []);

  const loadSub = useCallback(async () => {
    try {
      const { data } = await api.get("/souvenirs/subdispatch");
      setSubRows(data.rows); setSubSummary(data.summary); setSubDisc(data.discrepancies);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, []);

  const saveDispatch = async (region) => {
    setSavingRegion(region);
    try {
      const e = edits[region] || {};
      const { data } = await api.post("/souvenirs/dispatch", {
        region, tshirts: Number(e.tshirts) || 0, cloth: Number(e.cloth) || 0,
      });
      toast.success(data.reopened ? `Saved — ${region} must re-confirm` : `Dispatch saved for ${region}`);
      loadDispatch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingRegion(""); }
  };

  const addMore = async () => {
    setAddingMore(true);
    try {
      const t = addTarget;
      const { data } = await api.post("/souvenirs/dispatch/add", {
        region: t.region, tshirts: Number(t.tshirts) || 0, cloth: Number(t.cloth) || 0,
      });
      toast.success(data.reopened
        ? `Added — new totals ${data.tshirts}/${data.cloth}; ${t.region} must re-confirm`
        : `Added — new totals ${data.tshirts} t-shirts, ${data.cloth} cloth`);
      setAddTarget(null); loadDispatch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAddingMore(false); }
  };

  const saveTarget = async () => {
    setSavingTarget(true);
    try {
      const t = dispatchTarget;
      const { data } = await api.post("/souvenirs/subdispatch", {
        target_type: t.type, target_name: t.name, target_label: t.label,
        tshirts: Number(t.tshirts) || 0, cloth: Number(t.cloth) || 0,
      });
      toast.success(data.reopened ? "Saved — recipient must re-confirm" : "Dispatch saved");
      setDispatchTarget(null); loadSub(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingTarget(false); }
  };

  useEffect(() => { if (!isSub) api.get("/souvenirs/filters").then((r) => setFilters(r.data)).catch(() => {}); }, [isSub]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { group_by: groupBy };
      if (facility) params.facility = facility;
      else if (chapter) params.chapter = chapter;
      const { data } = await api.get("/souvenirs/summary", { params });
      setData(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [facility, chapter, groupBy]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isRegional && view === "breakdown") loadSub(); }, [isRegional, view, loadSub]);
  useEffect(() => {
    if (isNational && view === "dispatch") loadDispatch();
    if (isRegional && view === "received") loadReceived();
    if (view === "timeline") loadTimeline();
    if (isSub) loadSubReceived();
  }, [isNational, isRegional, isSub, view, loadDispatch, loadReceived, loadSubReceived, loadTimeline]);

  const exportBlob = async (url, name) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href; a.download = `${name}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click(); URL.revokeObjectURL(href);
    } catch { toast.error("Export failed"); }
  };

  const clearFilters = () => { setFacility(""); setChapter(""); };
  const hasFilter = facility || chapter;

  const DispatchBtn = ({ type, name, label }) => {
    const rec = (type === "facility" ? facMap : chMap)[name];
    return (
      <div className="flex items-center gap-2 justify-end flex-wrap">
        {rec ? <ItemBadges row={rec} /> : <span className="text-xs text-muted-foreground">Not dispatched</span>}
        <Button size="sm" variant={rec ? "outline" : "default"} className="gap-1.5"
          onClick={() => setDispatchTarget({ type, name, label, tshirts: rec?.tshirts ?? 0, cloth: rec?.cloth ?? 0 })}
          data-testid={`dispatch-target-${name}`}>
          <Truck size={14} weight="bold" /> {rec ? "Edit" : "Dispatch"}
        </Button>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="souvenirs-page">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground">Logistics</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1 flex items-center gap-3">
            <TShirt size={40} weight="duotone" className="text-primary" /> Souvenirs
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            {isSub ? "Souvenirs dispatched to you by your Regional Secretary." :
              `T-shirt sizes required across the union${filters.scoped_region ? ` — ${filters.scoped_region} Region` : ", grouped by region"}.`}
          </p>
        </div>

        {(isNational || isRegional || isSub) && (
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="max-w-full h-auto flex-wrap justify-start">
              <TabsTrigger value="breakdown" data-testid="stab-breakdown">{isSub ? "T-shirt Sizes" : "Size Breakdown"}</TabsTrigger>
              <TabsTrigger value="progress" data-testid="stab-progress">Distribution Progress</TabsTrigger>
              {isNational && <TabsTrigger value="dispatch" data-testid="stab-dispatch">Dispatched to regions</TabsTrigger>}
              {isRegional && <TabsTrigger value="received" data-testid="stab-received">Received from National</TabsTrigger>}
              {isSub && <TabsTrigger value="subreceived" data-testid="stab-subreceived">Received from Region</TabsTrigger>}
              {isSub && <TabsTrigger value="give" data-testid="stab-give">Give to Members</TabsTrigger>}
              {(isNational || isRegional) && <TabsTrigger value="membergifts" data-testid="stab-membergifts">Given to Members</TabsTrigger>}
              {(isNational || isRegional) && <TabsTrigger value="timeline" data-testid="stab-timeline">Timeline</TabsTrigger>}
            </TabsList>
          </Tabs>
        )}

        {/* ---------- Size Breakdown ---------- */}
        {view === "breakdown" && (
          <>
            <div className="flex flex-wrap gap-3" data-testid="souvenirs-total-cards">
              {data.sizes.map((s) => (
                <Card key={s} className="px-4 py-3 min-w-[86px]" data-testid={`total-card-${s}`}>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{s}</p>
                  <p className="text-2xl font-bold mt-0.5">{data.totals?.[s] ?? 0}</p>
                </Card>
              ))}
              <Card className="px-4 py-3 min-w-[110px] bg-primary text-primary-foreground">
                <p className="text-xs uppercase tracking-wider opacity-80">Total shirts</p>
                <p className="text-2xl font-bold mt-0.5" data-testid="grand-total">{data.grand_total}</p>
              </Card>
            </div>

            {!isSub && (
            <Card className="p-4">
              <div className="grid sm:grid-cols-2 gap-4 items-end">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Filter by Facility</label>
                  <SearchableSelect value={facility} onChange={(v) => { setFacility(v); setChapter(""); }}
                    options={filters.facilities} placeholder="All facilities" allLabel="All facilities"
                    testId="souvenirs-facility-filter" className="w-full" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Filter by Chapter</label>
                  <Select value={chapter || "__all__"} onValueChange={(v) => { setChapter(v === "__all__" ? "" : v); setFacility(""); }}>
                    <SelectTrigger data-testid="souvenirs-chapter-filter"><SelectValue placeholder="All chapters" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All chapters</SelectItem>
                      {filters.chapters.map((c) => <SelectItem key={c.prefix} value={c.prefix}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                {hasFilter && (
                  <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={clearFilters} data-testid="souvenirs-clear-filters">
                    <X size={14} /> Clear filters
                  </Button>
                )}
                {isNational && (
                  <Button size="sm" variant="outline" className="gap-1.5 ml-auto" onClick={() => exportBlob("/souvenirs/export", "souvenir_sizes")} data-testid="souvenirs-export-button">
                    <DownloadSimple size={16} weight="bold" /> Export to Excel
                  </Button>
                )}
              </div>
            </Card>
            )}

            {isRegional && subDisc.length > 0 && (
              <Card className="border-destructive/40 bg-destructive/5 p-4" data-testid="regional-discrepancies">
                <p className="font-semibold text-destructive flex items-center gap-2">
                  <Warning size={18} weight="fill" /> {subDisc.length} recipient(s) reported a shortage
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  {subDisc.map((d) => (
                    <li key={d.target} data-testid={`regional-discrepancy-${d.target}`}>
                      <span className="font-medium">{d.target}</span>:{" "}
                      {d.tshirts_short && <span>T-shirts {d.tshirts_received}/{d.tshirts} </span>}
                      {d.cloth_short && <span>Cloth {d.cloth_received}/{d.cloth} </span>}
                      {d.note && <span className="text-muted-foreground">— {d.note}</span>}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {isRegional && (
              <p className="text-sm text-muted-foreground -mb-4">
                Dispatch souvenirs to each facility/chapter below. Recipients confirm receipt from their own dashboard.
              </p>
            )}

            <Card className="p-0 overflow-hidden">
              <div className="overflow-x-auto hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 bg-card">{groupBy === "facility" ? "Facility" : "Region"}</TableHead>
                      {data.sizes.map((s) => <TableHead key={s} className="text-center">{s}</TableHead>)}
                      <TableHead className="text-center font-bold">Total</TableHead>
                      {isRegional && <TableHead className="text-right">Dispatch</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && <TableRow><TableCell colSpan={data.sizes.length + 3} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
                    {!loading && data.rows.length === 0 && (
                      <TableRow><TableCell colSpan={(data.sizes.length || 1) + 3} className="text-center py-8 text-muted-foreground">No T-shirt data for this selection yet.</TableCell></TableRow>
                    )}
                    {!loading && data.rows.map((row) => (
                      <TableRow key={row.name} data-testid={`souvenir-row-${row.name}`}>
                        <TableCell className="font-medium sticky left-0 bg-card">{row.name}</TableCell>
                        {data.sizes.map((s) => <TableCell key={s} className="text-center">{row.counts[s] || 0}</TableCell>)}
                        <TableCell className="text-center font-bold">{row.total}</TableCell>
                        {isRegional && <TableCell className="text-right"><DispatchBtn type="facility" name={row.name} label={row.name} /></TableCell>}
                      </TableRow>
                    ))}
                    {!loading && data.rows.length > 0 && (
                      <TableRow className="bg-muted/50 font-bold" data-testid="souvenir-totals-row">
                        <TableCell className="sticky left-0 bg-muted/50">All</TableCell>
                        {data.sizes.map((s) => <TableCell key={s} className="text-center">{data.totals[s] || 0}</TableCell>)}
                        <TableCell className="text-center">{data.grand_total}</TableCell>
                        {isRegional && <TableCell />}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile stacked cards */}
              <div className="md:hidden divide-y divide-border" data-testid="souvenirs-mobile-list">
                {loading && <p className="p-6 text-center text-muted-foreground">Loading…</p>}
                {!loading && data.rows.length === 0 && (
                  <p className="p-6 text-center text-muted-foreground">No T-shirt data for this selection yet.</p>
                )}
                {!loading && data.rows.map((row) => (
                  <div key={row.name} data-testid={`souvenir-card-${row.name}`} className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium">{row.name}</p>
                      <span className="text-sm font-bold shrink-0">{row.total} total</span>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 mt-3">
                      {data.sizes.filter((s) => row.counts[s]).map((s) => (
                        <div key={s} className="rounded-md bg-muted px-2 py-1 text-center">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{s}</p>
                          <p className="text-sm font-semibold">{row.counts[s]}</p>
                        </div>
                      ))}
                      {data.sizes.filter((s) => row.counts[s]).length === 0 && (
                        <p className="col-span-4 text-xs text-muted-foreground">No sizes recorded yet.</p>
                      )}
                    </div>
                    {isRegional && <div className="mt-3"><DispatchBtn type="facility" name={row.name} label={row.name} /></div>}
                  </div>
                ))}
              </div>
            </Card>

            {/* Chapters dispatch (regional) */}
            {isRegional && chapterRows.length > 0 && (
              <Card className="p-5" data-testid="chapters-dispatch">
                <p className="font-semibold mb-3">Chapters</p>
                <div className="space-y-3">
                  {chapterRows.map((c) => (
                    <div key={c.target_name} className="flex items-center justify-between gap-3 flex-wrap border-b border-border pb-3 last:border-0 last:pb-0"
                      data-testid={`chapter-dispatch-${c.target_name}`}>
                      <span className="font-medium">{c.target_label}</span>
                      <DispatchBtn type="chapter" name={c.target_name} label={c.target_label} />
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}

        {/* ---------- Distribution Progress (National) ---------- */}
        {view === "progress" && <DistributionProgress />}

        {/* ---------- Dispatched to regions (National) ---------- */}
        {isNational && view === "dispatch" && (
          <div className="space-y-4" data-testid="dispatch-view">
            <div className="flex flex-wrap gap-3" data-testid="dispatch-summary">
              <SummaryCard label="T-shirts dispatched" value={dSummary.tshirts_dispatched ?? 0} testId="sum-tshirts-dispatched" />
              <SummaryCard label="T-shirts confirmed" value={dSummary.tshirts_received ?? 0} testId="sum-tshirts-received" />
              <SummaryCard label="Cloth dispatched" value={dSummary.cloth_dispatched ?? 0} testId="sum-cloth-dispatched" />
              <SummaryCard label="Cloth confirmed" value={dSummary.cloth_received ?? 0} testId="sum-cloth-received" />
              <Button size="sm" variant="outline" className="gap-1.5 self-center ml-auto" onClick={() => exportBlob("/souvenirs/dispatch/export", "souvenir_dispatch")} data-testid="dispatch-export-button">
                <DownloadSimple size={16} weight="bold" /> Export
              </Button>
            </div>

            {dDisc.length > 0 && (
              <Card className="border-destructive/40 bg-destructive/5 p-4" data-testid="dispatch-discrepancies">
                <p className="font-semibold text-destructive flex items-center gap-2">
                  <Warning size={18} weight="fill" /> {dDisc.length} region(s) reported a shortage
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  {dDisc.map((d) => (
                    <li key={d.region} data-testid={`discrepancy-${d.region}`}>
                      <span className="font-medium">{d.region}</span>:{" "}
                      {d.tshirts_short && <span>T-shirts {d.tshirts_received}/{d.tshirts} </span>}
                      {d.cloth_short && <span>Cloth {d.cloth_received}/{d.cloth} </span>}
                      {d.note && <span className="text-muted-foreground">— {d.note}</span>}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <p className="text-sm text-muted-foreground">Enter t-shirts and cloth dispatched to each region. Regions confirm receipt (per item) from their dashboard.</p>

            <Card className="p-0 overflow-hidden hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Region</TableHead>
                    <TableHead className="text-center w-28">T-shirts</TableHead>
                    <TableHead className="text-center w-28">Cloth</TableHead>
                    <TableHead>Confirmation</TableHead>
                    <TableHead className="text-right w-24">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dispatch.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No regions found.</TableCell></TableRow>}
                  {dispatch.map((r) => (
                    <TableRow key={r.region} data-testid={`dispatch-row-${r.region}`}>
                      <TableCell className="font-medium">{r.region}</TableCell>
                      <TableCell className="text-center">
                        <Input type="number" min={0} value={edits[r.region]?.tshirts ?? 0}
                          onChange={(e) => setEdits((p) => ({ ...p, [r.region]: { ...p[r.region], tshirts: e.target.value } }))}
                          className="h-9 text-center" data-testid={`dispatch-tshirts-${r.region}`} />
                      </TableCell>
                      <TableCell className="text-center">
                        <Input type="number" min={0} value={edits[r.region]?.cloth ?? 0}
                          onChange={(e) => setEdits((p) => ({ ...p, [r.region]: { ...p[r.region], cloth: e.target.value } }))}
                          className="h-9 text-center" data-testid={`dispatch-cloth-${r.region}`} />
                      </TableCell>
                      <TableCell><ItemBadges row={r} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Button size="sm" disabled={savingRegion === r.region} onClick={() => saveDispatch(r.region)}
                            data-testid={`dispatch-save-${r.region}`} className="gap-1.5">
                            <PaperPlaneTilt size={14} weight="bold" /> Save
                          </Button>
                          {r.dispatched && (
                            <Button size="sm" variant="outline" onClick={() => setAddTarget({ region: r.region, tshirts: 0, cloth: 0 })}
                              data-testid={`dispatch-add-${r.region}`} className="gap-1.5">
                              <Plus size={14} weight="bold" /> Add
                            </Button>
                          )}
                          {r.dispatched && (
                            <Button size="sm" variant="ghost" onClick={() => openHistory(r.region)}
                              data-testid={`dispatch-history-${r.region}`} className="gap-1.5 text-muted-foreground">
                              <ClockCounterClockwise size={14} weight="bold" /> Log
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            <div className="md:hidden space-y-3" data-testid="dispatch-mobile-list">
              {dispatch.map((r) => (
                <Card key={r.region} className="p-4" data-testid={`dispatch-card-${r.region}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="font-medium">{r.region}</p>
                    <ItemBadges row={r} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">T-shirts</label>
                      <Input type="number" min={0} value={edits[r.region]?.tshirts ?? 0}
                        onChange={(e) => setEdits((p) => ({ ...p, [r.region]: { ...p[r.region], tshirts: e.target.value } }))}
                        className="h-9" data-testid={`dispatch-tshirts-mobile-${r.region}`} />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Cloth</label>
                      <Input type="number" min={0} value={edits[r.region]?.cloth ?? 0}
                        onChange={(e) => setEdits((p) => ({ ...p, [r.region]: { ...p[r.region], cloth: e.target.value } }))}
                        className="h-9" data-testid={`dispatch-cloth-mobile-${r.region}`} />
                    </div>
                  </div>
                  <Button size="sm" disabled={savingRegion === r.region} onClick={() => saveDispatch(r.region)}
                    data-testid={`dispatch-save-mobile-${r.region}`} className="w-full mt-3 gap-1.5">
                    <PaperPlaneTilt size={14} weight="bold" /> Save
                  </Button>
                  {r.dispatched && (
                    <Button size="sm" variant="outline" onClick={() => setAddTarget({ region: r.region, tshirts: 0, cloth: 0 })}
                      data-testid={`dispatch-add-mobile-${r.region}`} className="w-full mt-2 gap-1.5">
                      <Plus size={14} weight="bold" /> Add more
                    </Button>
                  )}
                  {r.dispatched && (
                    <Button size="sm" variant="ghost" onClick={() => openHistory(r.region)}
                      data-testid={`dispatch-history-mobile-${r.region}`} className="w-full mt-1 gap-1.5 text-muted-foreground">
                      <ClockCounterClockwise size={14} weight="bold" /> View log
                    </Button>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ---------- Received from National (Regional) ---------- */}
        {isRegional && view === "received" && (
          <div className="space-y-4" data-testid="received-view">
            {!received ? <p className="text-sm text-muted-foreground">Loading…</p>
              : !received.dispatched ? (
                <Card className="p-8 text-center border-dashed" data-testid="received-none">
                  <Package size={32} className="mx-auto text-muted-foreground" weight="duotone" />
                  <p className="mt-3 text-sm text-muted-foreground">No souvenirs have been dispatched to your region yet.</p>
                </Card>
              ) : <ReceivedPanel data={received} confirmUrl="/souvenirs/received/confirm" reload={loadReceived} testPrefix="received" />}
          </div>
        )}

        {/* ---------- Received from Region (Facility/Chapter) ---------- */}
        {isSub && view === "subreceived" && (
          <div className="space-y-4" data-testid="subreceived-view">
            {!subReceived ? <p className="text-sm text-muted-foreground">Loading…</p>
              : !subReceived.dispatched ? (
                <Card className="p-8 text-center border-dashed" data-testid="subreceived-none">
                  <Package size={32} className="mx-auto text-muted-foreground" weight="duotone" />
                  <p className="mt-3 text-sm text-muted-foreground">No souvenirs have been dispatched to you yet.</p>
                </Card>
              ) : <ReceivedPanel data={subReceived} confirmUrl="/souvenirs/subreceived/confirm" reload={loadSubReceived} testPrefix="subreceived" />}
          </div>
        )}

        {isSub && view === "give" && <GiveToMembers />}
        {(isNational || isRegional) && view === "membergifts" && <MemberGiftsReport />}

        {/* ---------- Timeline ---------- */}
        {(isNational || isRegional) && view === "timeline" && (
          <div className="space-y-3" data-testid="timeline-view">
            <p className="text-sm text-muted-foreground">Every dispatch and confirmation, most recent first.</p>
            {timeline.length === 0 && (
              <Card className="p-8 text-center border-dashed"><p className="text-sm text-muted-foreground">No activity yet.</p></Card>
            )}
            <div className="relative pl-5">
              {timeline.map((e, i) => (
                <div key={i} className="relative pb-5 last:pb-0" data-testid={`timeline-item-${i}`}>
                  <span className={`absolute -left-[13px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-background ${e.action === "dispatched" ? "bg-primary" : e.short ? "bg-destructive" : "bg-emerald-500"}`} />
                  <span className="absolute -left-[7px] top-4 bottom-0 w-px bg-border" />
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm">
                        <span className="font-semibold">{e.actor_name}</span>{" "}
                        {e.action === "dispatched" ? "dispatched to" : "confirmed receipt for"}{" "}
                        <span className="font-medium">{e.target_label}</span>
                        {e.tier === "sub" && e.region ? <span className="text-muted-foreground"> · {e.region}</span> : null}
                      </p>
                      <p className={`text-xs mt-0.5 ${e.short ? "text-destructive font-medium" : "text-muted-foreground"}`}>{e.detail}</p>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">{(e.at || "").slice(0, 10)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dispatch history (top-up log) dialog */}
        <Dialog open={!!historyTarget} onOpenChange={(o) => !o && setHistoryTarget(null)}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="dispatch-history-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Dispatch log — {historyTarget?.region}</DialogTitle>
              <DialogDescription>Every dispatch and top-up to this region, most recent first.</DialogDescription>
            </DialogHeader>
            {!historyTarget?.list ? <p className="text-sm text-muted-foreground">Loading…</p>
              : historyTarget.list.length === 0 ? <p className="text-sm text-muted-foreground">No dispatch activity yet.</p>
              : (
                <ul className="space-y-2">
                  {historyTarget.list.map((e, i) => (
                    <li key={i} className="rounded-md border border-border px-3 py-2" data-testid={`dispatch-history-item-${i}`}>
                      <p className="text-sm">{e.detail}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{e.actor_name} · {(e.at || "").slice(0, 10)}</p>
                    </li>
                  ))}
                </ul>
              )}
          </DialogContent>
        </Dialog>

        {/* Add-more (top-up) dialog — National dispatch to regions */}
        <Dialog open={!!addTarget} onOpenChange={(o) => !o && setAddTarget(null)}>
          <DialogContent className="max-w-sm" data-testid="dispatch-add-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Add souvenirs to {addTarget?.region}</DialogTitle>
              <DialogDescription>These quantities are added on top of what's already dispatched. If a total changes, that item goes back to pending confirmation.</DialogDescription>
            </DialogHeader>
            {addTarget && (
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="space-y-1.5">
                  <Label>Additional T-shirts</Label>
                  <Input type="number" min={0} value={addTarget.tshirts}
                    onChange={(e) => setAddTarget((p) => ({ ...p, tshirts: e.target.value }))}
                    data-testid="dispatch-add-tshirts" />
                </div>
                <div className="space-y-1.5">
                  <Label>Additional Cloth</Label>
                  <Input type="number" min={0} value={addTarget.cloth}
                    onChange={(e) => setAddTarget((p) => ({ ...p, cloth: e.target.value }))}
                    data-testid="dispatch-add-cloth" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddTarget(null)} disabled={addingMore}>Cancel</Button>
              <Button onClick={addMore} disabled={addingMore || (!Number(addTarget?.tshirts) && !Number(addTarget?.cloth))}
                className="gap-1.5" data-testid="dispatch-add-save">
                <Plus size={16} weight="bold" /> {addingMore ? "Adding…" : "Add to dispatch"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Regional dispatch dialog (facility/chapter) */}
        <Dialog open={!!dispatchTarget} onOpenChange={(o) => !o && setDispatchTarget(null)}>
          <DialogContent className="max-w-sm" data-testid="dispatch-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Dispatch to {dispatchTarget?.label}</DialogTitle>
              <DialogDescription>Enter the quantities you are sending. The recipient confirms receipt.</DialogDescription>
            </DialogHeader>
            {dispatchTarget && (
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="space-y-1.5">
                  <Label>T-shirts</Label>
                  <Input type="number" min={0} value={dispatchTarget.tshirts}
                    onChange={(e) => setDispatchTarget((p) => ({ ...p, tshirts: e.target.value }))}
                    data-testid="dispatch-dialog-tshirts" />
                </div>
                <div className="space-y-1.5">
                  <Label>Cloth (pieces)</Label>
                  <Input type="number" min={0} value={dispatchTarget.cloth}
                    onChange={(e) => setDispatchTarget((p) => ({ ...p, cloth: e.target.value }))}
                    data-testid="dispatch-dialog-cloth" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDispatchTarget(null)} disabled={savingTarget}>Cancel</Button>
              <Button onClick={saveTarget} disabled={savingTarget} className="gap-1.5" data-testid="dispatch-dialog-save">
                <Truck size={16} weight="bold" /> {savingTarget ? "Saving…" : "Save dispatch"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
